import { Avatar, Box, LinearProgress, Paper, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ApiClient from "../config/ApiClient";
import { ModelType } from "../object-actions/types/types";

const UserView = () => {
  const { id } = useParams();
  const theme = useTheme();

  const [entityData, updateData] = useState<{ data: ModelType<"Users"> } | string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const apiUrl = `/api/users/${id}`;
      try {
        const response = await ApiClient.get(apiUrl);
        if (response.error) {
          updateData(response.error);
        } else if (response.success && response.data) {
          updateData({ data: response.data as ModelType<"Users"> });
        }
      } catch (error) {
        updateData(error instanceof Error ? error.message : "Unknown error occurred");
      }
    };

    fetchData();
  }, [id]);

  if (!entityData) {
    return <LinearProgress />;
  }

  if (typeof entityData === "string") {
    return <div>{entityData}</div>;
  }

  const user = entityData.data;
  const fullName = user.real_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username;
  const avatarUrl = user.avatar || user.photo;
  const coverUrl = user.cover_photo;
  const bio = user.bio || user.summary;

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Cover Photo Section */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 200, sm: 300, md: 400 },
          position: "relative",
          overflow: "hidden",
          bgcolor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
        }}
      >
        {coverUrl ? (
          <Box
            component="img"
            src={coverUrl}
            alt="Cover"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              background: theme.palette.mode === "dark"
                ? "linear-gradient(135deg, rgba(12, 35, 64, 0.8) 0%, rgba(142, 181, 220, 0.3) 100%)"
                : "linear-gradient(135deg, rgba(135, 112, 16, 0.2) 0%, rgba(42, 116, 183, 0.2) 100%)",
            }}
          />
        )}
      </Box>

      {/* Profile Content */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          mt: { xs: -8, sm: -10, md: -12 },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            pt: { xs: 8, sm: 10, md: 12 },
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          {/* Avatar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Avatar
              src={avatarUrl || undefined}
              alt={fullName}
              sx={{
                width: { xs: 120, sm: 150, md: 180 },
                height: { xs: 120, sm: 150, md: 180 },
                border: `4px solid ${theme.palette.background.paper}`,
                boxShadow: theme.shadows[8],
                bgcolor: theme.palette.primary.main,
                fontSize: { xs: "3rem", sm: "4rem", md: "5rem" },
                fontWeight: "bold",
              }}
            >
              {!avatarUrl && fullName.charAt(0).toUpperCase()}
            </Avatar>
          </Box>

          {/* Full Name */}
          <Typography
            variant="h3"
            component="h1"
            align="center"
            sx={{
              mb: 3,
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {fullName}
          </Typography>

          {/* Bio/Summary */}
          {bio && (
            <Box
              sx={{
                maxWidth: 800,
                mx: "auto",
                mt: 4,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  lineHeight: 1.8,
                  color: "text.secondary",
                  whiteSpace: "pre-wrap",
                  textAlign: "center",
                }}
              >
                {bio}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default UserView;
